import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLocation } from "wouter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

type StatusFilter = "alle" | "neu" | "in_bearbeitung" | "abgeschlossen" | "fehler";

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  neu: { label: "Neu", color: "bg-blue-100 text-blue-800 border-blue-200" },
  in_bearbeitung: { label: "In Bearbeitung", color: "bg-yellow-100 text-yellow-800 border-yellow-200" },
  abgeschlossen: { label: "Abgeschlossen", color: "bg-green-100 text-green-800 border-green-200" },
  fehler: { label: "Fehler", color: "bg-red-100 text-red-800 border-red-200" },
};

export default function AdminAuftraege() {
  const { user, loading } = useAuth();
  const [, navigate] = useLocation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("alle");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [analyseUrl, setAnalyseUrl] = useState("");
  const [notiz, setNotiz] = useState("");
  const [analyseRunning, setAnalyseRunning] = useState(false);
  const [analyseResult, setAnalyseResult] = useState<Record<string, unknown> | null>(null);

  const utils = trpc.useUtils();

  const { data, isLoading, error } = trpc.admin.getAuftraege.useQuery(
    { status: statusFilter },
    { enabled: !!user && user.role === "admin" }
  );

  const { data: selectedData } = trpc.admin.getAuftrag.useQuery(
    { id: selectedId! },
    { enabled: selectedId !== null && !!user && user.role === "admin" }
  );

  const updateStatus = trpc.admin.updateStatus.useMutation({
    onSuccess: () => {
      utils.admin.getAuftraege.invalidate();
      utils.admin.getAuftrag.invalidate({ id: selectedId! });
      toast.success("Status aktualisiert");
    },
  });

  const startAnalyse = trpc.admin.startAnalyse.useMutation({
    onMutate: () => setAnalyseRunning(true),
    onSuccess: (res) => {
      setAnalyseRunning(false);
      if (res.success) {
        setAnalyseResult(res.data as Record<string, unknown>);
        toast.success("Analyse abgeschlossen! Ergebnis ist gespeichert.");
        utils.admin.getAuftraege.invalidate();
        utils.admin.getAuftrag.invalidate({ id: selectedId! });
      } else {
        toast.error(res.error ?? "Unbekannter Fehler bei der Analyse");
      }
    },
    onError: (err) => {
      setAnalyseRunning(false);
      toast.error(err.message);
    },
  });

  const saveNotiz = trpc.admin.saveNotiz.useMutation({
    onSuccess: () => {
      utils.admin.getAuftrag.invalidate({ id: selectedId! });
      toast.success("Notiz gespeichert");
    },
  });

  if (loading) return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="text-white">Lade...</div>
    </div>
  );

  if (!user || user.role !== "admin") {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center text-white">
          <h1 className="text-2xl font-bold mb-4">Kein Zugriff</h1>
          <p className="text-gray-400 mb-6">Dieses Dashboard ist nur für Administratoren.</p>
          <Button onClick={() => navigate("/")} variant="outline" className="border-white text-white hover:bg-white hover:text-black">
            Zur Startseite
          </Button>
        </div>
      </div>
    );
  }

  const auftraege = data?.auftraege ?? [];
  const counts = {
    alle: auftraege.length,
    neu: auftraege.filter(a => a.status === "neu").length,
    in_bearbeitung: auftraege.filter(a => a.status === "in_bearbeitung").length,
    abgeschlossen: auftraege.filter(a => a.status === "abgeschlossen").length,
    fehler: auftraege.filter(a => a.status === "fehler").length,
  };

  const auftrag = selectedData?.auftrag;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Header */}
      <div className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} className="text-gray-400 hover:text-white transition-colors text-sm">
              ← Zurück
            </button>
            <span className="text-gray-600">|</span>
            <h1 className="text-lg font-bold tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}>
              AUFTRAGS-DASHBOARD
            </h1>
          </div>
          <div className="text-sm text-gray-400">
            Eingeloggt als <span className="text-white font-medium">{user.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats-Leiste */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          {(["alle", "neu", "in_bearbeitung", "abgeschlossen", "fehler"] as StatusFilter[]).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`rounded-lg p-3 text-center transition-all border ${
                statusFilter === s
                  ? "bg-[#6d0000] border-[#6d0000] text-white"
                  : "bg-white/5 border-white/10 text-gray-300 hover:bg-white/10"
              }`}
            >
              <div className="text-2xl font-bold">{statusFilter === s ? auftraege.length : counts[s]}</div>
              <div className="text-xs mt-1 capitalize">{STATUS_LABELS[s]?.label ?? "Alle"}</div>
            </button>
          ))}
        </div>

        {/* Tabelle */}
        {isLoading ? (
          <div className="text-center py-16 text-gray-400">Lade Aufträge...</div>
        ) : error ? (
          <div className="text-center py-16 text-red-400">Fehler: {error.message}</div>
        ) : auftraege.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <div className="text-4xl mb-4">📭</div>
            <p>Keine Aufträge vorhanden.</p>
            <p className="text-sm mt-2 text-gray-500">Sobald jemand eine Analyse kauft, erscheint sie hier.</p>
          </div>
        ) : (
          <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-gray-400 text-xs uppercase tracking-wider">
                  <th className="text-left px-4 py-3">ID</th>
                  <th className="text-left px-4 py-3">Produkt</th>
                  <th className="text-left px-4 py-3">Kunde</th>
                  <th className="text-left px-4 py-3">Website</th>
                  <th className="text-left px-4 py-3">Betrag</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Datum</th>
                  <th className="text-left px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {auftraege.map((a) => (
                  <tr
                    key={a.id}
                    className="border-b border-white/5 hover:bg-white/5 transition-colors cursor-pointer"
                    onClick={() => {
                      setSelectedId(a.id);
                      setAnalyseUrl(a.targetUrl ?? "");
                      setNotiz(a.adminNotiz ?? "");
                      setAnalyseResult(null);
                    }}
                  >
                    <td className="px-4 py-3 text-gray-400">#{a.id}</td>
                    <td className="px-4 py-3 font-medium">{a.productName ?? a.productId}</td>
                    <td className="px-4 py-3 text-gray-300">{a.customerEmail ?? "—"}</td>
                    <td className="px-4 py-3 text-gray-300 max-w-[180px] truncate">
                      {a.targetUrl ? (
                        <a href={a.targetUrl} target="_blank" rel="noopener noreferrer" className="text-[#6d9999] hover:underline" onClick={e => e.stopPropagation()}>
                          {a.targetUrl.replace(/^https?:\/\//, "")}
                        </a>
                      ) : <span className="text-gray-600 italic">Nicht angegeben</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-300">
                      {a.amountPaid ? `${(a.amountPaid / 100).toFixed(2)} €` : "—"}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_LABELS[a.status]?.color ?? ""}`}>
                        {STATUS_LABELS[a.status]?.label ?? a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {new Date(a.createdAt).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-white/20 text-white hover:bg-white/10 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedId(a.id);
                          setAnalyseUrl(a.targetUrl ?? "");
                          setNotiz(a.adminNotiz ?? "");
                          setAnalyseResult(null);
                        }}
                      >
                        Öffnen
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail-Dialog */}
      <Dialog open={selectedId !== null} onOpenChange={(open) => { if (!open) { setSelectedId(null); setAnalyseResult(null); } }}>
        <DialogContent className="bg-[#111] border-white/20 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}>
              AUFTRAG #{auftrag?.id} — {auftrag?.productName ?? auftrag?.productId}
            </DialogTitle>
          </DialogHeader>

          {auftrag && (
            <div className="space-y-5">
              {/* Kunden-Info */}
              <div className="bg-white/5 rounded-lg p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Kunde</span>
                  <span>{auftrag.customerEmail ?? "—"}{auftrag.customerName ? ` (${auftrag.customerName})` : ""}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Betrag</span>
                  <span>{auftrag.amountPaid ? `${(auftrag.amountPaid / 100).toFixed(2)} €` : "—"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Stripe Session</span>
                  <span className="text-xs text-gray-500 truncate max-w-[200px]">{auftrag.stripeSessionId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Eingegangen</span>
                  <span>{new Date(auftrag.createdAt).toLocaleString("de-DE")}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">Status</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${STATUS_LABELS[auftrag.status]?.color ?? ""}`}>
                    {STATUS_LABELS[auftrag.status]?.label ?? auftrag.status}
                  </span>
                </div>
              </div>

              {/* Status ändern */}
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Status ändern</p>
                <div className="flex gap-2 flex-wrap">
                  {(["neu", "in_bearbeitung", "abgeschlossen", "fehler"] as const).map((s) => (
                    <Button
                      key={s}
                      size="sm"
                      variant={auftrag.status === s ? "default" : "outline"}
                      className={auftrag.status === s ? "bg-[#6d0000] border-[#6d0000]" : "border-white/20 text-white hover:bg-white/10"}
                      onClick={() => updateStatus.mutate({ id: auftrag.id, status: s })}
                      disabled={updateStatus.isPending}
                    >
                      {STATUS_LABELS[s].label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Analyse starten */}
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Analyse starten</p>
                <div className="flex gap-2">
                  <Input
                    value={analyseUrl}
                    onChange={(e) => setAnalyseUrl(e.target.value)}
                    placeholder="https://example.com"
                    className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 flex-1"
                  />
                  <Button
                    onClick={() => startAnalyse.mutate({ id: auftrag.id, url: analyseUrl })}
                    disabled={analyseRunning || !analyseUrl.trim()}
                    className="bg-[#115751] hover:bg-[#0d4440] text-white whitespace-nowrap"
                  >
                    {analyseRunning ? "Läuft..." : "Analyse starten"}
                  </Button>
                </div>
                {analyseRunning && (
                  <p className="text-xs text-yellow-400 mt-2">⏳ Analyse läuft — kann 30–90 Sekunden dauern...</p>
                )}
                {analyseResult && (
                  <div className="mt-3 bg-green-900/20 border border-green-500/30 rounded-lg p-3">
                    <p className="text-green-400 text-sm font-medium mb-2">✓ Analyse abgeschlossen</p>
                    <pre className="text-xs text-gray-300 overflow-auto max-h-48 whitespace-pre-wrap">
                      {JSON.stringify(analyseResult, null, 2)}
                    </pre>
                  </div>
                )}
                {auftrag.analyseErgebnis && !analyseResult && (
                  <div className="mt-3 bg-white/5 rounded-lg p-3">
                    <p className="text-xs text-gray-400 mb-2">Gespeichertes Ergebnis:</p>
                    <pre className="text-xs text-gray-300 overflow-auto max-h-48 whitespace-pre-wrap">
                      {auftrag.analyseErgebnis.substring(0, 500)}...
                    </pre>
                  </div>
                )}
              </div>

              {/* Notiz */}
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Interne Notiz</p>
                <Textarea
                  value={notiz}
                  onChange={(e) => setNotiz(e.target.value)}
                  placeholder="Notizen zum Auftrag..."
                  className="bg-white/10 border-white/20 text-white placeholder:text-gray-500 min-h-[80px]"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2 border-white/20 text-white hover:bg-white/10"
                  onClick={() => saveNotiz.mutate({ id: auftrag.id, notiz })}
                  disabled={saveNotiz.isPending}
                >
                  Notiz speichern
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
